import { useCallback, useEffect, useRef, useState } from 'react';
import contratosService from 'features/contratos/services/contratosService';
import contratosConsolidadoService from 'features/contratos/services/contratosConsolidadoService';
import { getCompanyContext } from 'utils/setCompanyContext';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => ({
  data_inicio: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
  data_fim: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0))
});

const resumoInicial = {
  totalContratos: 0,
  valorTotal: 0,
  faturamentoCpf: 0,
  faturamentoCnpj: 0,
  faturamentoPessoaFisica: 0,
  faturamentoPessoaJuridica: 0,
  ativos: 0,
  suspensos: 0,
  internetOk: 0,
  internetProblema: 0,
  pessoasFisicas: 0,
  pessoasJuridicas: 0,
  contratosBloqueioManual: 0,
  valorBloqueioManual: 0,
  titulosEmitidos: 0,
  valorEmitido: 0,
  valorAteVencimento: 0,
  descontoTotal: 0,
  titulosRecebidos: 0,
  valorRecebido: 0,
  titulosEmitidosPf: 0,
  valorEmitidoPf: 0,
  titulosRecebidosPf: 0,
  valorRecebidoPf: 0,
  titulosEmitidosPj: 0,
  valorEmitidoPj: 0,
  titulosRecebidosPj: 0,
  valorRecebidoPj: 0,
  cidades: [],
  planos: [],
  ...getDefaultDateRange()
};

const getEmpresaAtual = () => getCompanyContext()?.companies?.[0]?.name || 'MOV Fibra';

const useFaturamentoContratos = (filtrosData = getDefaultDateRange()) => {
  const [resumo, setResumo] = useState(resumoInicial);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const requestIdRef = useRef(0);

  const carregarResumo = useCallback(async (periodo = filtrosData) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const companyContext = getCompanyContext();
      const isConsolidated = companyContext?.isGroup || companyContext?.isConsolidated;
      const companies = Array.isArray(companyContext?.companies) ? companyContext.companies : [];
      const dateRange = getDefaultDateRange();
      const dataInicio = periodo?.data_inicio || periodo?.start || dateRange.data_inicio;
      const dataFim = periodo?.data_fim || periodo?.end || dateRange.data_fim;

      let resultado;

      if (isConsolidated && companies.length > 1) {
        const companyIds = companies
          .map((company) => company.name || company.id)
          .filter(Boolean);

        resultado = await contratosConsolidadoService.buscarDadosConsolidados(companyIds, {
          empresa: getEmpresaAtual(),
          status: 'A',
          cidade: 'todas',
          tipo_pessoa: 'todos',
          data_inicio: dataInicio,
          data_fim: dataFim
        });
      } else {
        resultado = await contratosService.buscarResumoContratos({
          empresa: getEmpresaAtual(),
          status: 'A',
          cidade: 'todas',
          tipo_pessoa: 'todos',
          data_inicio: dataInicio,
          data_fim: dataFim
        });
      }

      const resumoResultado = resultado?.data?.resumo || resultado?.resumo;

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (resultado?.success && resumoResultado) {
        setResumo(resumoResultado);
        setUltimaAtualizacao(new Date());
      } else {
        throw new Error('Resposta inválida do resumo de contratos');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar faturamento de contratos:', err);
      if (requestId === requestIdRef.current) {
        setError('Não foi possível carregar o faturamento de contratos.');
        setResumo(resumoInicial);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [filtrosData]);

  useEffect(() => {
    carregarResumo(filtrosData);
  }, [carregarResumo, filtrosData]);

  useEffect(() => {
    const handleCompanyChange = () => carregarResumo(filtrosData);
    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarResumo, filtrosData]);

  return {
    resumo,
    loading,
    initialLoading,
    error,
    ultimaAtualizacao,
    recarregar: carregarResumo
  };
};

export default useFaturamentoContratos;
